import React, { memo, useState } from 'react';
import { Box, Chip, ListItemText, MenuItem, Select, SelectProps } from '@mui/material';
import RoundCheckbox from '../round-checkbox/RoundCheckbox';
import DropdownArrowUpIcon from '../../../assets/svg-fc/DropdownArrowUpIcon';
import DropdownArrowDownIcon from '../../../assets/svg-fc/DropdownArrowDownIcon';
import palette from '../../../theme/palette';
import typography from '../../../theme/typography';
import { IDropdownOption } from '../../../types/commonTypes';
import './dropdownWithChips.scss';

export interface iDropdownWithChipsProps {
  disabled?: boolean;
  values: string[];
  options: IDropdownOption[];
  onChange: (values: string[]) => void;
  labelStyle?: React.CSSProperties;
  menuStyle?: React.CSSProperties;
  menuHorizontalPosition?: 'left' | 'center' | 'right' | number;
  selectProps?: SelectProps<any>;
  onOpen?: () => void;
  className?: string;
  dropdownIconColor?: string;
  placeholder?: string;
}

const DropdownWithChips = ({
  disabled,
  values,
  options,
  onChange,
  labelStyle,
  menuStyle,
  menuHorizontalPosition = 'center',
  selectProps,
  onOpen,
  className,
  dropdownIconColor,
  placeholder,
}: iDropdownWithChipsProps) => {
  const [open, setOpen] = useState<boolean>(false);

  const handleChange = (event: any) => {
    const {
      target: { value },
    } = event;

    // Handle "All" selection
    if (value.includes('all')) {
      if (values.length === options.length) {
        // Deselecting all - don't close dropdown
        event.stopPropagation();
        onChange([]);
      } else {
        // Selecting all - close dropdown
        onChange(options.map((opt) => opt.id));
        setOpen(false);
      }
    } else {
      onChange(typeof value === 'string' ? value.split(',') : value);
    }
  };

  return (
    <Select
      sx={{
        backgroundColor: 'transparent',
        '& .MuiSelect-select': {
          fontFamily: typography.fontFamily,
          fontWeight: typography.fontWeightMedium,
          color: palette.secondary.main,
          fontSize: '16px',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '8px',
          ...labelStyle,
        },
      }}
      MenuProps={{
    slotProps: {
      paper: {
      style: {
        maxHeight: 300,
        top: 20,
        ...menuStyle,
      },
          },
        },
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: menuHorizontalPosition,
        },
        transformOrigin: {
          vertical: 'top',
          horizontal: menuHorizontalPosition,
        },
      }}
      disabled={disabled}
      multiple
      displayEmpty
      className={`dropdownWithChips ${className || ''}`}
      value={values}
      onChange={handleChange}
      onOpen={() => {
        onOpen?.();
        setTimeout(() => setOpen(true), onOpen ? 500 : 0);
      }}
      onClose={() => setOpen(false)}
      onClick={(e) => e.stopPropagation()}
      open={open}
      renderValue={(selected) => {
        return selected.length === 0 ? (
          <p className="downdownPlaceholder" style={{ color: palette.grey['600'], margin: 0 }}>{placeholder}</p>
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {(selected as string[]).map((value) => (
              <Chip key={value} label={options.find((option) => option.id === value)?.value} className="selectedChip" />
            ))}
          </Box>
        );
      }}
      IconComponent={(e) =>
        open ? (
          <DropdownArrowUpIcon
            color={dropdownIconColor}
            className={`customDropDownIcon ${e.className}`}
            onClick={() => setOpen(false)}
          />
        ) : (
          <DropdownArrowDownIcon
            color={dropdownIconColor}
            className={`customDropDownIcon ${e.className}`}
            onClick={() => setOpen(true)}
          />
        )
      }
      {...selectProps}
    >
      <MenuItem
        className="all-option"
        value="all"
        onClick={(e) => e.stopPropagation()}
      >
        <RoundCheckbox
          checked={values.length === options.length && options.length > 0}
        />
        <ListItemText primary="All" />
      </MenuItem>
      {options?.map((option) => {
        const isSelected = values.includes(option.id);
        return (
          <MenuItem
            sx={{
              fontFamily: typography.fontFamily,
              fontWeight: typography.fontWeightMedium,
              color: palette.secondary.main,
              fontSize: '16px',
              padding: '10px 20px',
              gap: '12px',
              '&.Mui-selected': {
                backgroundColor: palette.background.paper,
              },
            }}
            key={option.id}
            value={option.id}
          >
            <RoundCheckbox checked={isSelected} />
            <ListItemText primary={option.value} />
          </MenuItem>
        );
      })}
    </Select>
  );
};

export default memo(DropdownWithChips);
